import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract, faCalendarAlt, faTag, faArrowUp,
  faStore, faExchangeAlt, faBan, faGavel, faMoneyBillWave,
  faShieldAlt, faBalanceScale, faEnvelope, faPhone, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import '../assets/css/legal-pages.css';

const TermsAndConditions = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="legal-page">
      {/* Hero Header */}
      <div className="legal-header">
        <FontAwesomeIcon icon={faFileContract} className="legal-header-icon" />
        <h1>Terms and Conditions</h1>
        <div className="legal-header-meta">
          <span><FontAwesomeIcon icon={faCalendarAlt} /> Effective Date: 6th March, 2026</span>
          <span><FontAwesomeIcon icon={faTag} /> Version 1.0</span>
        </div>
        <div className="legal-intro">
          These Terms and Conditions govern all access to and use of the AutoboyExpress website and mobile application, including all purchase and swap transactions. By creating an account or accessing the Platform in any manner, you acknowledge that you have read, understood, and agreed to be legally bound by these Terms and Conditions in their entirety. If you do not agree to these Terms, you must not access or use the Platform.
        </div>
      </div>

      {/* 1. Definitions */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faTag} /> 1. Definitions</h2>
        <ul>
          <li><strong>"Platform"</strong> — the AutoboyExpress website and mobile application.</li>
          <li><strong>"AutoboyExpress", "AutoBoy", "Company", "we", "our", "us"</strong> — Autoboyexpress and its authorized representatives.</li>
          <li><strong>"User", "you", "your"</strong> — any person who accesses or uses the Platform, whether as a buyer, seller, or visitor.</li>
          <li><strong>"Buyer"</strong> — any user who initiates a purchase or swap transaction on the Platform.</li>
          <li><strong>"Seller"</strong> — any user who lists a product for sale or swap on the Platform.</li>
          <li><strong>"Listing"</strong> — any product posted on the Platform for sale or swap.</li>
          <li><strong>"Escrow"</strong> — the secure payment holding system managed by AutoboyExpress on behalf of transacting parties.</li>
          <li><strong>"Swap Transaction"</strong> — a transaction where a buyer offers their own device in exchange for a seller's listed item, with or without an additional top-up payment.</li>
          <li><strong>"Standard Purchase"</strong> — a direct purchase transaction where the buyer pays the full listed price for an item.</li>
          <li><strong>"FabsTech Express"</strong> — the third-party logistics partner responsible for pickup, warehouse inspection, and delivery of all orders.</li>
        </ul>
      </div>

      {/* 2. Eligibility */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 2. Eligibility</h2>
        <ul>
          <li>You must be at least 18 years of age to register or use the Platform.</li>
          <li>You must have the legal capacity to enter into binding contracts under Nigerian law.</li>
          <li>By using the Platform, you confirm that all information provided during registration and throughout your use of the Platform is accurate, current, and truthful.</li>
          <li>AutoboyExpress reserves the right to refuse access or terminate accounts where eligibility requirements are not met.</li>
        </ul>
      </div>

      {/* 3. Platform Role */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faStore} /> 3. Platform Role and Limitations</h2>
        <p>AutoboyExpress operates exclusively as a digital marketplace and escrow intermediary. The Company:</p>
        <ul>
          <li>Provides a secure escrow payment system for all transactions</li>
          <li>Coordinates logistics and delivery through FabsTech Express</li>
          <li>Facilitates structured, privacy-protected communication between buyers and sellers</li>
          <li>Conducts physical warehouse inspection of all items prior to final delivery</li>
        </ul>
        <p>AutoboyExpress is not the manufacturer, owner, or direct seller of any listed product unless expressly stated. The Company does not guarantee the accuracy of seller-provided product descriptions and shall not be held liable for disputes arising from misrepresentation by either party, subject to the dispute resolution process outlined in Section 10.</p>
      </div>

      {/* 4. Account Registration */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 4. Account Registration and Privacy</h2>
        <ul>
          <li>Users must register using accurate and complete information. False or misleading registration information constitutes a breach of these Terms.</li>
          <li>Each user is assigned a public username. Buyers and sellers will only see each other's public usernames during transactions. No personal addresses, phone numbers, or direct contact details are ever shared between parties through the Platform.</li>
          <li>Users are solely responsible for maintaining the confidentiality of their login credentials. Any activity occurring under your account is your responsibility.</li>
          <li>AutoboyExpress reserves the right to verify user identity and may request supporting documentation for KYC compliance in accordance with applicable Nigerian financial regulations.</li>
          <li>AutoboyExpress reserves the right to suspend or permanently terminate accounts that violate these Terms, engage in fraudulent activity, or pose a risk to other users or the Platform.</li>
        </ul>
      </div>

      {/* 5. Standard Purchase */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faStore} /> 5. Standard Purchase Flow</h2>

        <h3>5.1 Product Discovery</h3>
        <p>Buyers may browse listings through search, category filters, or the homepage. Each listing displays the product photographs, description, condition, price, and the seller's public profile. No private seller information is disclosed at any stage.</p>

        <h3>5.2 Order Summary</h3>
        <p>Prior to payment, the buyer is presented with a full order summary which includes:</p>
        <ul>
          <li>Product name and condition</li>
          <li>Seller's public profile name</li>
          <li>Buyer's selected delivery address</li>
          <li>Logistics fee — calculated in real time via FabsTech Express based on delivery location</li>
          <li>VAT at 7.5% in accordance with applicable Nigerian tax law</li>
          <li>Grand total payable</li>
        </ul>
        <p>The buyer is charged as: <strong>Product Price + Logistics Fee + VAT (7.5%)</strong></p>
        <p>Platform fees are not charged to buyers in standard purchase transactions. Platform fees are deducted solely from the seller's payout.</p>

        <h3>5.3 Order Cancellation Policy — Standard Purchase</h3>
        <div className="legal-warning">
          ONCE A STANDARD PURCHASE ORDER HAS BEEN PLACED AND PAYMENT CONFIRMED, THE ORDER CANNOT BE CANCELLED BY THE BUYER UNDER ANY CIRCUMSTANCES.
        </div>
        <p>This policy exists to protect sellers from loss of time, logistics costs, and opportunity. Exceptions apply only where:</p>
        <ul>
          <li>The item fails physical inspection at the AutoBoy warehouse</li>
          <li>The item materially differs from its listing description</li>
          <li>Fraud or misrepresentation is established following a formal dispute investigation</li>
        </ul>

        <h3>5.4 Escrow Payment</h3>
        <p>All payments are processed through Monnify or direct bank transfer. Where escrow applies, funds are held by Pandascrow upon payment confirmation. The seller does not receive any funds until the order is successfully completed and confirmed by both parties.</p>

        <h3>5.5 Standard Purchase Platform Fee Tiers</h3>
        <p>Platform fees are deducted exclusively from the seller's payout. The buyer is not informed of the platform fee structure.</p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Order Amount</th><th>Platform Charge</th></tr>
            </thead>
            <tbody>
              <tr><td>₦10,000 – ₦90,000</td><td>30%</td></tr>
              <tr><td>₦100,000 – ₦199,000</td><td>20%</td></tr>
              <tr><td>₦200,000 – ₦299,000</td><td>8%</td></tr>
              <tr><td>₦300,000 – ₦399,000</td><td>7%</td></tr>
              <tr><td>₦400,000 – ₦499,000</td><td>4%</td></tr>
              <tr><td>₦500,000 and above</td><td>3%</td></tr>
            </tbody>
          </table>
        </div>

        <h3>5.6 Logistics, Warehouse Inspection, and Delivery</h3>
        <p>All standard purchase orders follow this mandatory three-stop logistics flow:</p>
        <ol>
          <li><strong>Seller</strong> — FabsTech rider collects item from the seller</li>
          <li><strong>AutoBoy Warehouse</strong> — Item is delivered to the AutoBoy inspection centre for physical verification</li>
          <li><strong>Buyer</strong> — Inspected item is delivered to the buyer's address</li>
        </ol>
        <p>At no point does the seller see the buyer's delivery address, nor does the buyer see the seller's address.</p>

        <h3>5.7 Delivery Confirmation and Payment Release</h3>
        <p>Funds are automatically released to the seller <strong>3 days after confirmed delivery</strong>, provided no dispute has been raised. If a dispute is raised within the 3-day window, all funds are held until the dispute is resolved by AutoboyExpress admin.</p>
        <p>The net amount released to the seller is calculated as: <strong>Product Price − Platform Fee − VAT = Seller Net Payout</strong></p>
      </div>

      {/* 6. Swap Order Flow */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faExchangeAlt} /> 6. Swap Order Flow</h2>

        <h3>6.1 Who Can Initiate a Swap</h3>
        <p>Any registered user may initiate a swap on a listing marked as 'Swap Available'. The party initiating the swap is referred to as the swap initiator.</p>

        <h3>6.2 Swap Submission Requirements</h3>
        <p>To initiate a swap, the initiator must submit a completed swap form containing:</p>
        <ul>
          <li>Device type — iOS, Android, or Laptop</li>
          <li>Device model and storage capacity</li>
          <li>Condition rating on a scale of 1 to 10</li>
          <li>Battery health percentage — required for iPhone devices</li>
          <li>Face ID or Touch ID operational status — required for iPhone devices</li>
          <li>Full repair and modification history</li>
          <li>A minimum of 5 clear photographs or videos showing all sides of the device, the screen, and the Settings or About page</li>
        </ul>

        <h3>6.3 Seller Review and Decision</h3>
        <p>Upon receiving a swap offer, the seller may Decline the offer or Accept it and enter a top-up amount the initiator must pay to complete the swap.</p>

        <h3>6.4 Swap Platform Charge Tiers</h3>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Swap Amount</th><th>Platform Charge</th></tr>
            </thead>
            <tbody>
              <tr><td>₦10,000 – ₦90,000</td><td>50%</td></tr>
              <tr><td>₦100,000 – ₦199,000</td><td>40%</td></tr>
              <tr><td>₦200,000 – ₦299,000</td><td>10%</td></tr>
              <tr><td>₦300,000 – ₦399,000</td><td>9%</td></tr>
              <tr><td>₦400,000 – ₦499,000</td><td>6%</td></tr>
              <tr><td>₦500,000 and above</td><td>5%</td></tr>
            </tbody>
          </table>
        </div>
        <p>VAT at 7.5% applies to all swap transactions and is included in the initiator's total payable amount.</p>

        <h3>6.5 Initiator's Response to Seller Offer</h3>
        <p>Once the seller sends the price offer, the initiator may Decline or Accept. If accepted, the initiator is directed to the checkout page to complete payment.</p>

        <h3>6.6 Swap Order Cancellation Policy</h3>
        <div className="legal-warning">
          ONCE A SWAP ORDER HAS BEEN ACCEPTED BY THE INITIATOR AND PAYMENT HAS BEEN CONFIRMED, THE ORDER CANNOT BE CANCELLED UNDER ANY CIRCUMSTANCES.
        </div>
        <p>Exceptions apply only where: a swap device fails physical inspection, a device materially differs from the submitted swap description, or fraud or misrepresentation is established following a formal dispute investigation.</p>

        <h3>6.7 Swap Checkout</h3>
        <p>The initiator's checkout summary includes: swap top-up fee, logistics fee calculated via FabsTech Express, and grand total payable. Payment is accepted via Monnify, wallet balance, or direct bank transfer.</p>

        <h3>6.8 Swap Completion</h3>
        <p>Upon payment confirmation, funds are held securely. Both devices are collected, inspected at the AutoBoy warehouse, and delivered to their respective parties. Upon confirmation of receipt by both parties, funds are released to the seller minus the platform fee and VAT.</p>
        <p>In the event a dispute is resolved in favour of the initiator, only the swap top-up fee will be refunded. Logistics fees are non-refundable once the delivery process has been initiated.</p>
      </div>

      {/* 7. Device Clearing */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 7. Device Clearing Obligation</h2>
        <p>Before a rider collects any device, the seller or swap initiator is required to confirm that the device has been fully cleared of all personal data, including but not limited to:</p>
        <ul>
          <li>iCloud account and Apple ID — for iOS devices</li>
          <li>Google account — for Android devices</li>
          <li>Banking and financial applications</li>
          <li>Cryptocurrency wallets</li>
          <li>Social media accounts</li>
          <li>Saved passwords and biometric data</li>
        </ul>
        <p>Failure to clear a device before handover is the sole responsibility of the user. AutoboyExpress accepts no liability for data loss, unauthorised access, or financial loss resulting from failure to clear personal data before device transfer.</p>
      </div>

      {/* 8. Prohibited Activities */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBan} /> 8. Prohibited Activities</h2>
        <p>Users are strictly prohibited from engaging in any of the following:</p>
        <ul>
          <li>Listing, selling, or swapping counterfeit, stolen, or illegally obtained goods</li>
          <li>Conducting transactions outside the Platform to bypass the escrow system</li>
          <li>Sharing personal contact details with the intent to arrange off-platform transactions</li>
          <li>Submitting false, misleading, or fraudulent product descriptions or swap device information</li>
          <li>Misrepresenting device condition, repair history, or specifications in swap submissions</li>
          <li>Engaging in chargeback abuse, payment fraud, or identity fraud</li>
          <li>Manipulating reviews, ratings, or feedback</li>
          <li>Creating multiple accounts to circumvent suspension or platform restrictions</li>
          <li>Harassing, threatening, or abusing other users or platform staff</li>
        </ul>
        <p>Violations may result in immediate account suspension, permanent termination, forfeiture of held funds pending investigation, and referral to relevant law enforcement authorities.</p>
      </div>

      {/* 9. Want to Buy / Random Items */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faStore} /> 9. Want to Buy and Random Items</h2>
        <h3>9.1 Want to Buy</h3>
        <p>Registered users may post a 'Want to Buy' request specifying the device they are seeking, their budget range, and location preference. Sellers may respond by linking a relevant active listing. AutoboyExpress does not guarantee that a response will be received. The buyer's contact details are never exposed to sellers at any stage.</p>
        <h3>9.2 Random Items</h3>
        <p>The Random Items category is reserved exclusively for AutoboyExpress administrators. Regular sellers are not permitted to post in this category. Random Items may surface as recommended add-ons during checkout.</p>
      </div>

      {/* 10. Dispute Resolution */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faGavel} /> 10. Dispute Resolution</h2>
        <ul>
          <li>Any dispute arising from a transaction must be formally raised <strong>within 3 days of confirmed delivery</strong> through the Platform's dispute submission interface.</li>
          <li>The disputing party must provide verifiable supporting evidence, including photographs, videos, screenshots, and written descriptions of the issue.</li>
          <li>AutoboyExpress will investigate all disputes and reserves the right to make a final determination at its sole discretion. The Company's decision shall be binding on both parties.</li>
          <li>During the investigation period, all funds related to the disputed transaction will remain held and inaccessible to either party.</li>
          <li>AutoboyExpress reserves the right to extend the investigation period where necessary and will communicate updates to both parties through the Platform.</li>
        </ul>
      </div>

      {/* 11. Refunds */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> 11. Refunds</h2>
        <p>Refunds may be granted exclusively in the following circumstances:</p>
        <ul>
          <li>An item fails physical inspection at the AutoBoy warehouse prior to delivery</li>
          <li>An item materially differs from its listing description upon warehouse inspection</li>
          <li>A swap device fails verification or materially differs from the submitted swap description</li>
          <li>Fraud or misrepresentation is formally established following a dispute investigation</li>
        </ul>
        <p>Refunds will be processed through the original payment method. For swap transactions, only the swap top-up fee is refundable. Logistics fees are non-refundable once the delivery process has commenced.</p>
      </div>

      {/* 12. Platform Fees & VAT */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> 12. Platform Fees and Value Added Tax</h2>
        <p>Platform fees are deducted from the seller's payout on all completed transactions. Buyers are not charged platform fees on standard purchases. The applicable platform fee tier is determined by the transaction value as detailed in Sections 5.5 and 6.4.</p>
        <p>Value Added Tax (VAT) at <strong>7.5%</strong> is applied to all transactions on the Platform in accordance with applicable Nigerian tax laws.</p>
      </div>

      {/* 13. Intellectual Property */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 13. Intellectual Property</h2>
        <p>All content on the Platform, including the AutoboyExpress name, logo, interface design, graphics, and written content, is the exclusive property of AutoboyExpress and is protected under applicable Nigerian and international intellectual property laws.</p>
        <p>Users retain ownership of content they upload to the Platform. By uploading content, users grant AutoboyExpress a non-exclusive, royalty-free licence to display, reproduce, and distribute such content solely for the purpose of operating and promoting the Platform.</p>
      </div>

      {/* 14. Limitation of Liability */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBalanceScale} /> 14. Limitation of Liability</h2>
        <p>To the fullest extent permitted by applicable law:</p>
        <ul>
          <li>AutoboyExpress shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of the Platform.</li>
          <li>The Company's maximum aggregate liability in connection with any transaction shall not exceed the total value of that specific transaction.</li>
          <li>AutoboyExpress does not warrant uninterrupted, error-free, or secure access to the Platform and accepts no liability for downtime, data loss, or technical failures caused by circumstances beyond its reasonable control.</li>
        </ul>
      </div>

      {/* 15. KYC */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 15. Regulatory Compliance and KYC</h2>
        <p>AutoboyExpress operates in alignment with applicable financial regulations issued by the Central Bank of Nigeria. The Company reserves the right to implement identity verification and Know Your Customer (KYC) procedures at any stage, including:</p>
        <ul>
          <li>National Identity Number (NIN) verification via Youverify</li>
          <li>Bank Verification Number (BVN) — collected from sellers and passed directly to Monnify for bank account name verification. AutoboyExpress does not store or process BVN data directly.</li>
          <li>Corporate Affairs Commission (CAC) verification for seller business accounts via Youverify</li>
        </ul>
        <p>Failure to complete required verification procedures may result in account restrictions or suspension.</p>
      </div>

      {/* 16. Modifications */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faFileContract} /> 16. Modifications to These Terms</h2>
        <p>AutoboyExpress reserves the right to amend or update these Terms and Conditions at any time. Where material changes are made, registered users will be notified via email or in-app notification. Continued use of the Platform following notification of changes constitutes acceptance of the revised Terms.</p>
      </div>

      {/* 17. Account Suspension */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBan} /> 17. Account Suspension and Termination</h2>
        <ul>
          <li>AutoboyExpress reserves the right to suspend or permanently terminate any account without prior notice where a violation of these Terms is suspected or confirmed.</li>
          <li>Escrow funds may be temporarily withheld pending investigation into suspected fraud or violation.</li>
          <li>Users may request account deletion by contacting the Company directly. Pending transactions must be fully resolved before an account deletion request can be processed.</li>
          <li>Termination of an account does not relieve the user of any obligations or liabilities incurred prior to termination.</li>
        </ul>
      </div>

      {/* 18. Governing Law */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faGavel} /> 18. Governing Law and Jurisdiction</h2>
        <p>These Terms and Conditions shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria. Any disputes arising under or in connection with these Terms that cannot be resolved through the Platform's dispute resolution process shall be subject to the exclusive jurisdiction of the courts of Lagos State, Nigeria.</p>
      </div>

      {/* Contact */}
      <div className="legal-contact">
        <h2><FontAwesomeIcon icon={faEnvelope} /> 19. Contact Information</h2>
        <div className="legal-contact-grid">
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
            <span>3/9 Olukolesho Street, Tayeolu Tower, Lagos, Nigeria</span>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
            <a href="mailto:support@autoboyexpress.com">support@autoboyexpress.com</a>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faPhone} className="contact-icon" />
            <a href="tel:09136941527">09136941527</a> / <a href="tel:08110749313">08110749313</a>
          </div>
        </div>
      </div>

      <div className="legal-back-top">
        <button onClick={scrollToTop}>
          <FontAwesomeIcon icon={faArrowUp} /> Back to Top
        </button>
      </div>
    </div>
  );
};

export default TermsAndConditions;
